import { i18n } from "../translate/i18n";
import { parseTemplateComponents } from "./templateVariables";
import { parseTemplateComponentsInput } from "./templateSubmitValidation";

const PROMOTIONAL_PATTERNS = [
  /\boferta(s)?\b/,
  /\bdescuento(s)?\b/,
  /\bpromocion(es)?\b/,
  /\bpromo(s)?\b/,
  /\bexclusiv[oa]s?\b/,
  /\bgratis\b/,
  /\bgratuito(s)?\b/,
  /\bahorr(a|o|ar)\b/,
  /\brebaja(s)?\b/,
  /\bliquidacion(es)?\b/,
  /\bcupon(es)?\b/,
  /\b\d+\s*%\s*(de\s+)?descuento\b/,
  /\bpor\s+tiempo\s+limitado\b/,
  /\bultimas?\s+unidades\b/,
  /\bno\s+te\s+pierdas\b/,
  /\baprovecha\b/,
  /\boffer(s)?\b/,
  /\bdiscount(s)?\b/,
  /\bsale(s)?\b/,
  /\bexclusive\b/,
  /\bfree\b/,
  /\bdeal(s)?\b/,
  /\bpromotion(s)?\b/,
  /\bcoupon(s)?\b/,
  /\blimited\s+time\b/,
  /\bspecial\s+offer\b/,
  /\boff\s+your\b/,
];

const SALES_CTA_PATTERNS = [
  /\bcompra\s+ahora\b/,
  /\bcomprar\b/,
  /\badquiere\b/,
  /\bvisita\s+nuestro\b/,
  /\bconoce\s+mas\b/,
  /\bdescubre\b/,
  /\bregistrate\b/,
  /\bsuscribete\b/,
  /\bhaz\s+clic\b/,
  /\bclick\s+abajo\b/,
  /\bshop\s+now\b/,
  /\bbuy\s+now\b/,
  /\blearn\s+more\b/,
  /\bcheck\s+out\b/,
  /\bexplore\b/,
  /\bsign\s+up\b/,
  /\bget\s+yours\b/,
];

const TRANSACTIONAL_PATTERNS = [
  /\bpedido(s)?\b/,
  /\borden(es)?\b/,
  /\border(s)?\b/,
  /\benvio(s)?\b/,
  /\bentrega(s)?\b/,
  /\btracking\b/,
  /\brastreo\b/,
  /\bguia\b/,
  /\bcita(s)?\b/,
  /\breserva(s)?\b/,
  /\bbooking(s)?\b/,
  /\bappointment(s)?\b/,
  /\bpago(s)?\b/,
  /\bpayment(s)?\b/,
  /\bfactura(s)?\b/,
  /\binvoice(s)?\b/,
  /\bcuenta(s)?\b/,
  /\baccount(s)?\b/,
  /\bconfirmacion(es)?\b/,
  /\bconfirm(ed|ation)?\b/,
  /\brecibo(s)?\b/,
  /\breceipt(s)?\b/,
  /\bticket(s)?\b/,
  /\bsoporte\b/,
  /\bsupport\b/,
  /\brecordatorio\b/,
  /\breminder\b/,
  /\bestatus\b/,
  /\bestado\b/,
  /\bstatus\b/,
  /\bactualizacion(es)?\b/,
  /\bupdate(s)?\b/,
  /\breembolso(s)?\b/,
  /\brefund(s)?\b/,
  /\bverificacion\b/,
  /\bverification\b/,
];

const GENERIC_GREETING_PATTERNS = [
  /^hola\b/i,
  /^buenos?\s+dias?\b/i,
  /^buenas?\s+tardes?\b/i,
  /^buenas?\s+noches?\b/i,
  /^hi\b/i,
  /^hello\b/i,
  /\bcomo\s+estas\b/,
  /\bhow\s+are\s+you\b/,
  /\bespero\s+que\s+te\s+encuentres\b/,
  /\bhope\s+you('re|\s+are)\s+well\b/,
];

const ENTHUSIASTIC_PATTERNS = [
  /\bemocionad[oa]s?\b/,
  /\bexcited\b/,
  /\bgran\s+noticia\b/,
  /\bgreat\s+news\b/,
  /\bfelicitaciones\b/,
  /\bcongratulations\b/,
  /\btenemos\s+una\s+sorpresa\b/,
  /\bwe\s+have\s+a\s+surprise\b/,
];

function normalizeText(text) {
  return String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function matchesAnyPattern(text, patterns) {
  return patterns.some((pattern) => pattern.test(text));
}

export function collectTemplateContentTextsFromForm(form) {
  const texts = [];
  if (form?.headerText?.trim()) texts.push(form.headerText.trim());
  if (form?.bodyText?.trim()) texts.push(form.bodyText.trim());
  if (form?.footerText?.trim()) texts.push(form.footerText.trim());
  for (const button of form?.buttons ?? []) {
    if (button?.text?.trim()) texts.push(button.text.trim());
    if (button?.url?.trim()) texts.push(button.url.trim());
  }
  return texts;
}

export function collectTemplateContentTextsFromComponents(rawComponents) {
  const components = parseTemplateComponents(parseTemplateComponentsInput(rawComponents));
  const texts = [];

  for (const component of components) {
    if (!component || typeof component !== "object") continue;
    if (typeof component.text === "string" && component.text.trim()) {
      texts.push(component.text.trim());
    }
    const type = String(component.type ?? "").toUpperCase();
    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      for (const button of component.buttons) {
        if (button?.text?.trim()) texts.push(button.text.trim());
        if (button?.url?.trim()) texts.push(button.url.trim());
      }
    }
  }

  return texts;
}

export function evaluateTemplateCategoryHints(category, texts) {
  const normalizedCategory = String(category ?? "").toUpperCase();
  if (normalizedCategory === "AUTHENTICATION" || !texts?.length) {
    return [];
  }

  const combined = normalizeText(texts.join(" "));
  if (!combined.trim()) {
    return [];
  }

  const hints = [];
  const promotional = matchesAnyPattern(combined, PROMOTIONAL_PATTERNS);
  const salesCta = matchesAnyPattern(combined, SALES_CTA_PATTERNS);
  const transactional = matchesAnyPattern(combined, TRANSACTIONAL_PATTERNS);
  const genericGreeting = matchesAnyPattern(combined, GENERIC_GREETING_PATTERNS);
  const enthusiastic = matchesAnyPattern(combined, ENTHUSIASTIC_PATTERNS);

  if (normalizedCategory === "UTILITY") {
    if (promotional) {
      hints.push({ code: "utility_promotional_language", severity: "warning" });
    }
    if (salesCta) {
      hints.push({ code: "utility_sales_cta", severity: "warning" });
    }
    if (genericGreeting && !transactional) {
      hints.push({ code: "utility_generic_greeting", severity: "warning" });
    }
    if (!transactional) {
      hints.push({ code: "utility_missing_transaction_context", severity: "warning" });
    }
    if (enthusiastic) {
      hints.push({ code: "utility_enthusiastic_tone", severity: "warning" });
    }
    if (hints.length >= 2) {
      hints.unshift({ code: "utility_likely_reclassified", severity: "warning" });
    }
  }

  if (normalizedCategory === "MARKETING") {
    if (transactional && !promotional && !salesCta && !enthusiastic) {
      hints.push({ code: "marketing_looks_utility", severity: "info" });
    }
  }

  const seen = new Set();
  return hints.filter((hint) => {
    if (seen.has(hint.code)) return false;
    seen.add(hint.code);
    return true;
  });
}

export function evaluateTemplateCategoryHintsFromForm(category, form) {
  return evaluateTemplateCategoryHints(
    category,
    collectTemplateContentTextsFromForm(form),
  );
}

export function evaluateTemplateCategoryHintsFromComponents(category, rawComponents) {
  return evaluateTemplateCategoryHints(
    category,
    collectTemplateContentTextsFromComponents(rawComponents),
  );
}

export function translateTemplateCategoryHint(hint) {
  if (!hint?.code) return "";
  const key = `whatsappTemplates.categoryHints.${hint.code}`;
  return i18n.exists(key) ? i18n.t(key) : hint.code;
}

export function translateTemplateCategoryHints(hints) {
  return (hints ?? []).map(translateTemplateCategoryHint).filter(Boolean);
}

export function evaluateTemplateRowCategoryHints(template) {
  const hints = evaluateTemplateCategoryHintsFromComponents(
    template?.category,
    template?.components,
  );
  return {
    hints,
    messages: translateTemplateCategoryHints(hints),
    hasWarnings: hints.some((hint) => hint.severity === "warning"),
  };
}
